import { useState } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { v4 } from '@rotra-air-ocean/rotranext-shared-code';
import { Plus } from 'lucide-react';

import {
  availableFieldsAt,
  findField,
  hasUntrackedAncestor,
  resolveTemplatePath,
  stepWarnings,
} from '$layouts/v2/helpers/context';
import { useAccessToken } from '$src/authConfig';

import { Stack } from '../Stack';
import { WorkflowCanvas } from '../WorkflowCanvas';

import { AddStepModal } from './AddStepModal';
import { ConnectSlotModal } from './ConnectSlotModal';
import { KIND_DEFAULTS } from './constants';
import { CreateSubWorkflowModal } from './CreateSubWorkflowModal';
import type { WorkflowEditorProps } from './editor.types';
import { StepForm } from './StepForm';
import { useSubWorkflowMeta } from './useSubWorkflowMeta';
import { WorkflowInputsModal } from './WorkflowInputsModal';

export type { WorkflowEditorProps } from './editor.types';

/** Coordinates the canvas, selected step form, and workflow-level dialogs. */
export function WorkflowEditor({
  spec,
  meta,
  workflowId,
  title,
  trigger,
  workflowIds,
  validationIssues = [],
  selectedStepId,
  readOnly = false,
  onSelectStep,
  onChange,
}: WorkflowEditorProps) {
  const { token } = useAccessToken();
  const [adding, setAdding] = useState<false | true | string>(false);
  const [creatingSub, setCreatingSub] = useState(false);
  const [editingInputs, setEditingInputs] = useState(false);
  const [connect, setConnect] = useState<{
    source: string;
    target: string;
  } | null>(null);
  const canEdit = !readOnly;
  const stepIds = Object.keys(spec.steps);

  const stepOptions = stepIds.map((id) => ({
    id,
    label: spec.steps[id]?.label || id,
    kind: spec.steps[id]?.kind,
  }));
  const selected = selectedStepId ? spec.steps[selectedStepId] : undefined;
  const availableStepOptions = stepOptions.filter(
    (opt) => opt.id !== selectedStepId,
  );

  const availableFields = selectedStepId
    ? availableFieldsAt(trigger, spec, selectedStepId)
    : [];
  const subWorkflowId =
    selected?.kind === 'subWorkflow' ? selected.workflowId : undefined;
  const subMeta = useSubWorkflowMeta(subWorkflowId, token);
  const selectedSubMeta = subWorkflowId ? subMeta[subWorkflowId] : undefined;
  const selectedSubVersion =
    selected?.kind === 'subWorkflow' && selected.workflowVersion != null
      ? selectedSubMeta?.versions.find(
          (version) => version.version === selected.workflowVersion,
        )
      : undefined;

  const selectedWarnings =
    selectedStepId && selected
      ? stepWarnings(trigger, spec, selectedStepId, selected)
      : [];
  if (selectedStepId && selected?.kind === 'subWorkflow') {
    if (!selected.workflowId) {
      selectedWarnings.push('Choose a reusable workflow to run.');
    } else if (selectedSubMeta) {
      if (selectedSubMeta.exists) {
        if (selectedSubMeta.trigger !== 'workflow') {
          selectedWarnings.push(
            `Workflow "${selected.workflowId}" is email-triggered and cannot be used as a sub-workflow.`,
          );
        }
        if (selected.workflowVersion == null) {
          selectedWarnings.push(
            'Choose the active or an archived sub-workflow version.',
          );
        } else if (!selectedSubVersion) {
          selectedWarnings.push(
            `Workflow "${selected.workflowId}" version ${selected.workflowVersion} is unavailable or is still a draft.`,
          );
        }
        const inputNames = new Set(
          (selectedSubVersion?.spec.inputs ?? []).map((input) => input.name),
        );
        const outputNames = new Set(
          (selectedSubVersion?.spec.outputs ?? []).map((output) => output.name),
        );
        for (const input of selectedSubVersion?.spec.inputs ?? []) {
          if (!input.optional && !selected.inputs?.[input.name]?.trim()) {
            selectedWarnings.push(
              `Required input "${input.name}" is not mapped.`,
            );
          }
        }
        for (const [inputName, template] of Object.entries(
          selected.inputs ?? {},
        )) {
          if (!inputNames.has(inputName)) {
            selectedWarnings.push(
              `Input mapping "${inputName}" is no longer declared by the sub-workflow.`,
            );
            continue;
          }
          const untracked = hasUntrackedAncestor(spec, selectedStepId);
          for (const match of template.matchAll(
            new RegExp(v4.TEMPLATE_REFERENCE_RE.source, 'g'),
          )) {
            const resolution = resolveTemplatePath(availableFields, match[1]);
            if (
              !resolution.known &&
              !(untracked && resolution.errorCode === 'unknown-root')
            ) {
              selectedWarnings.push(
                `Input mapping "${inputName}" references "${match[1]}". ${
                  resolution.reason ?? 'That data is not available here.'
                }`,
              );
            }
          }
        }
        for (const outputName of Object.keys(selected.outputs ?? {})) {
          if (!outputNames.has(outputName)) {
            selectedWarnings.push(
              `Output mapping "${outputName}" is no longer declared by the sub-workflow.`,
            );
          }
        }
      } else {
        selectedWarnings.push(
          `Workflow "${selected.workflowId}" does not exist.`,
        );
      }
    }
    selectedWarnings.push(
      ...validationIssues
        .filter(
          (issue) =>
            issue.workflowId === workflowId && issue.stepId === selectedStepId,
        )
        .map((issue) => issue.message),
    );
  }
  const uniqueSelectedWarnings = [...new Set(selectedWarnings)];

  const update = (next: Partial<v4.WorkflowSpec>) => {
    if (!canEdit) {
      return;
    }

    const nextSpec = { ...spec, ...next };
    onChange({ ...nextSpec });
  };

  const patchStep = (id: string, patch: Partial<v4.StepSpec>) =>
    update({
      steps: {
        ...spec.steps,
        [id]: { ...spec.steps[id], ...patch } as v4.StepSpec,
      },
    });

  const addStep = (id: string, kind: v4.StepKind) => {
    if (!canEdit) {
      return;
    }
    const step: v4.StepSpec = {
      kind,
      label: id,
      ...KIND_DEFAULTS[kind],
    } as v4.StepSpec;

    let sourceStep: v4.StepSpec | undefined;

    // NOTE: for types which can be automatically connected, we update the source step to point to the new step.
    if (typeof adding === 'string') {
      sourceStep = spec.steps[adding];

      switch (sourceStep.kind) {
        case 'auto':
        case 'apiCall':
        case 'outlookCategory':
        case 'llm':
        case 'systemOne':
        case 'userInput': {
          sourceStep.next = id;
          break;
        }
        case 'subWorkflow': {
          sourceStep.onDone = id;
          break;
        }
      }
    }
    update({
      steps: {
        ...spec.steps,
        ...(sourceStep && typeof adding === 'string'
          ? { [adding]: sourceStep }
          : {}),
        [id]: step,
      },
    });
    onSelectStep(id);

    // NOTE: for steps which cannot be automatically connect, trigger dialog to connect.
    if (typeof adding === 'string') {
      sourceStep = spec.steps[adding];

      switch (sourceStep.kind) {
        case 'branch':
        case 'decision': {
          setConnect({ source: adding, target: id });
          break;
        }
      }
    }

    setAdding(false);
  };

  const removeStep = (id: string) => {
    if (!canEdit) {
      return;
    }
    if (!globalThis.confirm(`Delete step "${id}"?`)) {
      return;
    }
    const steps = { ...spec.steps };
    delete steps[id];
    update({ steps });
  };

  // Wire a transition by dragging between nodes on the canvas. Single-outcome
  // kinds set their target directly; branch/decision ask which slot to use.
  const handleConnect = (source: string, target: string) => {
    if (!canEdit) {
      return;
    }
    const step = spec.steps[source];
    if (!step || source === target) {
      return;
    }
    switch (step.kind) {
      case 'auto':
      case 'apiCall':
      case 'outlookCategory':
      case 'llm':
      case 'systemOne':
      case 'userInput': {
        patchStep(source, { next: target });
        break;
      }
      case 'subWorkflow': {
        patchStep(source, { onDone: target });
        break;
      }
      case 'branch':
      case 'decision': {
        setConnect({ source, target });
        break;
      }
      case 'final': {
        break;
      }
    }
  };

  // Remove a transition when its edge is deleted on the canvas.
  const handleClear = (source: string, target: string, label: string) => {
    if (!canEdit) {
      return;
    }
    const step = spec.steps[source];
    if (!step) {
      return;
    }
    switch (step.kind) {
      case 'auto':
      case 'apiCall':
      case 'outlookCategory':
      case 'llm':
      case 'systemOne':
      case 'userInput': {
        if (step.next === target) {
          patchStep(source, { next: '' });
        }
        break;
      }
      case 'subWorkflow': {
        if (step.onDone === target) {
          patchStep(source, { onDone: '' });
        }
        break;
      }
      case 'decision': {
        const actions = step.actions.filter((action) => action.next !== target);
        if (actions.length !== step.actions.length) {
          patchStep(source, { actions });
        }
        break;
      }
      case 'branch': {
        const patch: Partial<v4.StepSpec> = {};
        if (
          label === 'default' &&
          step.default === target &&
          'default' in patch
        ) {
          patch.default = '';
        } else {
          const cases = { ...step.cases };
          if (label in cases && cases[label] === target) {
            delete cases[label];
          } else {
            for (const [caseValue, caseTarget] of Object.entries(cases)) {
              if (caseTarget === target) {
                delete cases[caseValue];
              }
            }
          }

          if ('cases' in patch) {
            patch.cases = cases;
          }
        }
        patchStep(source, patch);
        break;
      }
    }
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: '1fr 0.5fr',
        },
        gap: 2,
        height: '100%',
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <WorkflowCanvas
          spec={spec}
          title={title}
          workflowId={workflowId}
          trigger={trigger}
          mode={canEdit ? 'edit' : 'inspect'}
          selectedStepId={selectedStepId}
          onSelectStep={onSelectStep}
          onConnect={canEdit ? handleConnect : undefined}
          onClearTransition={canEdit ? handleClear : undefined}
          onAddStep={
            canEdit ? (source?: string) => setAdding(source ?? true) : undefined
          }
          onSetInitial={canEdit ? (initial) => update({ initial }) : undefined}
          onEditInputs={
            canEdit && trigger === 'workflow'
              ? () => setEditingInputs(true)
              : undefined
          }
          onViewInputs={
            !canEdit && trigger === 'workflow'
              ? () => setEditingInputs(true)
              : undefined
          }
        />
      </Paper>

      <Paper
        variant="outlined"
        component="aside"
        sx={{ overflow: 'auto', p: 2, maxHeight: '89vh' }}
      >
        {!selected || !selectedStepId ? (
          <Stack spacing={2} sx={{ py: 4, textAlign: 'center' }}>
            <Typography
              sx={{
                color: 'text.secondary',
              }}
            >
              {canEdit
                ? 'Select a step on the diagram to edit it, or add a new one.'
                : 'Select a step on the diagram to inspect it.'}
            </Typography>
            {canEdit && (
              <Button
                variant="contained"
                startIcon={<Plus size={14} />}
                onClick={() => setAdding(true)}
              >
                Add step
              </Button>
            )}
          </Stack>
        ) : (
          <StepForm
            id={selectedStepId}
            step={selected}
            meta={meta}
            stepOptions={availableStepOptions}
            workflowIds={workflowIds}
            availableFields={availableFields}
            warnings={uniqueSelectedWarnings}
            isInitial={spec.initial === selectedStepId}
            readOnly={readOnly}
            subInputs={
              selected.kind === 'subWorkflow' && selected.workflowId
                ? selectedSubVersion?.spec.inputs
                : undefined
            }
            subOutputs={
              selected.kind === 'subWorkflow' && selected.workflowId
                ? selectedSubVersion?.spec.outputs
                : undefined
            }
            subVersions={selectedSubMeta?.versions}
            subActiveVersion={selectedSubMeta?.activeVersion}
            onPatch={(patch) => patchStep(selectedStepId, patch)}
            onRemove={() => removeStep(selectedStepId)}
          />
        )}
      </Paper>

      {canEdit && adding && (
        <AddStepModal
          existingIds={stepIds}
          onCancel={() => setAdding(false)}
          onAdd={addStep}
        />
      )}
      {editingInputs && (
        <WorkflowInputsModal
          inputs={spec.inputs ?? []}
          outputs={spec.outputs ?? []}
          inputTypes={meta.fieldInputTypes}
          onClose={() => setEditingInputs(false)}
          onChangeInputs={(inputs) => update({ inputs })}
          onChangeOutputs={(outputs) => update({ outputs })}
          readOnly={readOnly}
        />
      )}
      {canEdit && creatingSub && selectedStepId && (
        <CreateSubWorkflowModal
          onCancel={() => setCreatingSub(false)}
          onCreated={(childId) => {
            patchStep(selectedStepId, { workflowId: childId });
            setCreatingSub(false);
          }}
        />
      )}
      {canEdit &&
        connect &&
        spec.steps[connect.source] &&
        (() => {
          const step = spec.steps[connect.source];
          const enumOptions =
            step.kind === 'branch'
              ? findField(
                  availableFieldsAt(trigger, spec, connect.source),
                  step.selectField,
                )?.options
              : undefined;
          return (
            <ConnectSlotModal
              source={connect.source}
              target={connect.target}
              targetLabel={spec.steps[connect.target]?.label || connect.target}
              step={step as v4.BranchStepSpec | v4.DecisionStepSpec}
              enumOptions={enumOptions}
              onCancel={() => setConnect(null)}
              onApply={(patch) => {
                patchStep(connect.source, patch);
                setConnect(null);
              }}
            />
          );
        })()}
    </Box>
  );
}
